import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ShopItemType,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Title5 } from '@sorare/core/src/atoms/typography';
import ResponsiveImg from '@sorare/core/src/atoms/ui/ResponsiveImg';
import { ClubShopItem } from '@sorare/core/src/components/clubShop/ClubShopItem';
import { ClubShopItemFooter } from '@sorare/core/src/components/clubShop/ClubShopItem/ClubShopItemFooter';
import { ClubShopItemStatus } from '@sorare/core/src/components/clubShop/ClubShopItemStatus';
import { ClubShopItemsGrid } from '@sorare/core/src/components/clubShop/ClubShopItemsGrid';
import Dialog from '@sorare/core/src/components/dialog';
import { ASSETS_HOST } from '@sorare/core/src/config';
import useUpdateSportProfile from '@sorare/core/src/hooks/useUpdateSportProfile';
import { shopCategories } from '@sorare/core/src/lib/glossary';
import { skins } from '@sorare/core/src/lib/skins';

import { Banner } from '../Banner';
import { Logo } from '../Logo';

type Props = {
  sport: Sport;
  bannerUrl: string | null | undefined;
  shieldUrl: string | null | undefined;
  clubName: string | null | undefined;
};

export const ClubSkinEditor = ({
  sport,
  bannerUrl,
  shieldUrl,
  clubName,
}: Props) => {
  const [pickingSkin, setPickingSkin] = useState<
    ShopItemType.BANNER | ShopItemType.SHIELD
  >();
  const updateSportProfile = useUpdateSportProfile();

  const currentPickingSkinUrl = pickingSkin
    ? {
        [ShopItemType.BANNER]: bannerUrl,
        [ShopItemType.SHIELD]: shieldUrl,
      }[pickingSkin]
    : null;

  return (
    <>
      <Banner
        pictureUrl={bannerUrl}
        alt={clubName || ''}
        onClick={() => setPickingSkin(ShopItemType.BANNER)}
      />
      <Logo
        overlap
        pictureUrl={shieldUrl}
        alt={clubName || ''}
        onClick={() => setPickingSkin(ShopItemType.SHIELD)}
      />
      {pickingSkin && (
        <Dialog
          open
          maxWidth="sm"
          fullWidth
          onClose={() => setPickingSkin(undefined)}
          title={
            <Title5>
              <FormattedMessage {...shopCategories[pickingSkin]} />
            </Title5>
          }
        >
          <ClubShopItemsGrid
            inDialog
            items={skins.filter(
              skin => skin.skinType === pickingSkin && skin.sport === sport
            )}
            renderItem={skin => (
              <ClubShopItem
                key={skin.id}
                inDialog
                image={
                  <ResponsiveImg
                    width={pickingSkin === ShopItemType.BANNER ? 640 : 80}
                    cropWidth={pickingSkin === ShopItemType.BANNER ? 640 : 80}
                    src={`${ASSETS_HOST}${skin.pictureUrl}`}
                    alt={skin.name}
                  />
                }
                footer={
                  <ClubShopItemFooter
                    name={skin.name}
                    status={
                      <ClubShopItemStatus
                        status={
                          currentPickingSkinUrl ===
                          `${ASSETS_HOST}${skin.pictureUrl}`
                            ? 'equipped'
                            : 'owned'
                        }
                      />
                    }
                  />
                }
                onClick={() => {
                  updateSportProfile({
                    sport,
                    ...(pickingSkin === ShopItemType.BANNER
                      ? {
                          bannerColor: skin.bannerColor,
                          bannerUrl: `${ASSETS_HOST}${skin.pictureUrl}`,
                        }
                      : {}),
                    ...(pickingSkin === ShopItemType.SHIELD
                      ? { shieldUrl: `${ASSETS_HOST}${skin.pictureUrl}` }
                      : {}),
                  });
                }}
              />
            )}
          />
        </Dialog>
      )}
    </>
  );
};
